import { useState, useEffect, useId } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, LayoutGrid } from "lucide-react";
import toast from "react-hot-toast";
import { getProjectById, getProjectIFCModelReport, updateProjectStatus } from "../../api/Projects";
import { useAuth } from "../../contexts/AuthContext";
import type { Project } from "../../utils/interface/project";
import { getFileNameFromDownloadResponse, extractApiError, isSafeUrl } from "../../utils/util";
import { ProjectStatus } from "../../utils/interface/project";
import UserProfileCard from "../../components/UserProfileCard";
import defaultProjectImage from "../../assets/Projects/project1.png";

const DEFAULT_PROJECT_IMAGE = defaultProjectImage;

const TrackProgress = () => {
  const clipId = useId();
  return (
  <svg
    className="w-7 transition-transform duration-500 ease-in-out group-hover:rotate-180"
    id="Group_59"
    data-name="Group 59"
    xmlns="http://www.w3.org/2000/svg"
    width="27.164"
    height="27.154"
    viewBox="0 0 27.164 27.154"
  >
    <defs>
      <clipPath id={clipId}>
        <rect
          id="Rectangle_65"
          data-name="Rectangle 65"
          width="27.164"
          height="27.154"
          fill="#fff"
        />
      </clipPath>
    </defs>
    <g id="Group_58" data-name="Group 58" clipPath={`url(#${clipId})`}>
      <path
        id="Path_201"
        data-name="Path 201"
        d="M475.85,8.9l-5.837,1.9a7.459,7.459,0,0,0-6.423-4.662V0a13.589,13.589,0,0,1,12.26,8.9"
        transform="translate(-449.509)"
        fill="#fff"
      />
      <path
        id="Path_202"
        data-name="Path 202"
        d="M39.39,0V6.137A7.461,7.461,0,0,0,32.966,10.8L27.13,8.9A13.589,13.589,0,0,1,39.39,0"
        transform="translate(-26.306)"
        fill="#fff"
      />
      <path
        id="Path_203"
        data-name="Path 203"
        d="M613.3,328.051a13.558,13.558,0,0,1-5.2,10.689l-3.6-4.959a7.468,7.468,0,0,0,2.452-7.555l5.836-1.9a13.589,13.589,0,0,1,.516,3.721"
        transform="translate(-586.14 -314.479)"
        fill="#fff"
      />
      <path
        id="Path_204"
        data-name="Path 204"
        d="M8.8,333.771,5.2,338.73A13.6,13.6,0,0,1,.516,324.32l5.836,1.9A7.467,7.467,0,0,0,8.8,333.771"
        transform="translate(0 -314.469)"
        fill="#fff"
      />
      <path
        id="Path_205"
        data-name="Path 205"
        d="M212.911,659.749a13.589,13.589,0,0,1-15.152,0l3.6-4.958a7.461,7.461,0,0,0,7.946,0Z"
        transform="translate(-191.753 -634.902)"
        fill="#fff"
      />
    </g>
  </svg>
  );
};
const ACCENT = "#E761A0";
const BORDER_LIGHT = "#868686";
const DIVIDER = "#868686";

/** Pie-chart style progress: filled % = (completed stages / total stages) * 100. e.g. stage 2 = 40% */
function StageProgressGraph({
  completedStages,
  totalStages,
  size = 100,
  strokeWidth = 10,
}: {
  completedStages: number;
  totalStages: number;
  size?: number;
  strokeWidth?: number;
}) {
  const r = (size - strokeWidth) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  const percentage = totalStages > 0 ? Math.round((completedStages / totalStages) * 100) : 0;
  const dashoffset = circumference * (1 - completedStages / totalStages);

  return (
    <div className="relative inline-flex items-center justify-center w-full h-full">
      <svg
        viewBox={`0 0 ${size} ${size}`}
        className="absolute inset-0 w-full h-full"
        style={{ transform: "rotate(-90deg)" }}
        preserveAspectRatio="xMidYMid meet"
      >
        {/* Track (empty circle) */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={BORDER_LIGHT}
          strokeWidth={strokeWidth}
          opacity={0.35}
        />
        {/* Filled progress (like pie chart segment) */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={ACCENT}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashoffset}
          className="transition-all duration-500 ease-out"
        />
      </svg>
      {/* Center percentage label */}
      <span
        className="relative z-10 text-2xl sm:text-3xl font-bold tabular-nums"
        style={{ color: ACCENT }}
      >
        {percentage}%
      </span>
    </div>
  );
}

const DownloadIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="30"
    viewBox="0 0 24 30"
    className="text-white"
  >
    {/* File body → white → pink */}
    <path
      d="M3 2h12l6 6v18a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z"
      fill="currentColor"
      className="transition-colors duration-300 group-hover:text-[#E761A0]"
    />

    {/* Fold */}
    <path
      d="M15 2v6h6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="transition-colors duration-300 group-hover:text-[#E761A0]"
    />

    {/* Arrow group */}
    <g
      className="
        text-[#E761A0]
        transition-all duration-300 ease-out
        group-hover:translate-y-4
        group-hover:opacity-0
        group-hover:text-white
      "
    >
      <line
        x1="12"
        y1="11"
        x2="12"
        y2="17"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />

      <polyline
        points="9,14 12,17 15,14"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>

    {/* Bottom line */}
    <line
      x1="7"
      y1="22"
      x2="17"
      y2="22"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      className="text-[#E761A0] group-hover:text-white transition-colors duration-300"
    />
  </svg>
);
export const STAGES = [
  {
    id: 1,
    key: "file-uploaded",
    label: "Stage 1: IFC Uploaded",
    description: "User uploaded IFC file",
    current: false,
  },
  {
    id: 2,
    key: "processing",
    label: "Stage 2: Processing Started",
    description: "Automatic processing script running",
    current: false,
  },
  {
    id: 3,
    key: "result-ready",
    label: "Stage 3: Result Generated",
    description: "Processing completed and result generated",
    current: false,
  },
  {
    id: 4,
    key: "admin-review",
    label: "Stage 4: Admin Review",
    description: "Admin reviewing generated result",
    current: false,
  },
  {
    id: 5,
    key: "report-uploaded",
    label: "Stage 5: Report Uploaded",
    description: "Reviewed report uploaded by admin",
    current: false,
  },
];

const STAGE_COUNT = STAGES.length;
const MIN_STAGE_ID = 1;
const MAX_STAGE_ID = STAGE_COUNT;

/** Map stage id to API status key for PATCH projects/status */
const STAGE_ID_TO_STATUS: Record<number, string> = Object.fromEntries(
  STAGES.map((s) => [s.id, s.key])
);

/** Map backend project status to highest completed stage (1–5) for initial checked state */
function statusToCompletedStage(status: string | undefined): number {
  if (!status) return 0;
  const statusLower = status.toLowerCase();
  const byKey: Record<string, number> = {
    "file-uploaded": 1,
    processing: 2,
    "result-ready": 3,
    "admin-review": 4,
    "report-uploaded": 5,
  };
  if (byKey[statusLower] != null) return byKey[statusLower];
  const legacyOrder: Record<string, number> = {
    [ProjectStatus.DRAFT]: 0,
    [ProjectStatus.FILE_UPLOADED]: 1,
    [ProjectStatus.ADMIN_REVIEW]: 4,
    [ProjectStatus.ADMIN_REJECTED]: 0,
    [ProjectStatus.ACCEPTED_FOR_PROCESSING]: 2,
    [ProjectStatus.SCRIPT_RUNNING]: 2,
    [ProjectStatus.INVALID_FILE]: 0,
    [ProjectStatus.RESULT_GENERATED]: 3,
    [ProjectStatus.ADMIN_RESULT_REVIEW]: 4,
    [ProjectStatus.REPORT_UPLOADED]: 5,
    [ProjectStatus.DONE]: 5,
  };
  const val = legacyOrder[status];
  return val !== undefined && val !== null ? val : 0;
}

function initialCheckedFromStatus(status: string | undefined): Record<number, boolean> {
  const highest = statusToCompletedStage(status);
  return {
    ...Object.fromEntries(STAGES.map((s) => [s.id, false])),
    ...Object.fromEntries(
      Array.from({ length: STAGE_COUNT }, (_, i) => [i + 1, i + 1 <= highest])
    ),
  };
}

/** Prefer report.reportUrlPreview (signed URL); fallback to ifcModelReport / ifcModelReportPreview for backwards compatibility. */
function getReportDownloadUrl(project: Project & { ifcModelReport?: string | { url?: string; downloadUrl?: string } | null; ifcModelReportPreview?: string | null }): string | undefined {
  if (project.report?.reportUrlPreview) return project.report.reportUrlPreview;
  if (project.ifcModelReportPreview && typeof project.ifcModelReportPreview === "string") return project.ifcModelReportPreview;
  const raw = project.ifcModelReport;
  if (Array.isArray(raw)) {
    const first = raw[0] as { ifcModelReportPreview?: string; s3_uri_path?: string } | null | undefined;
    return first?.ifcModelReportPreview ?? first?.s3_uri_path ?? undefined;
  }
  if (typeof raw === "string") return raw || undefined;
  const obj = raw as { url?: string; downloadUrl?: string } | null | undefined;
  return obj?.url ?? obj?.downloadUrl ?? undefined;
}

const IFCProcessing = () => {
  const { id } = useParams<{ id: string }>();
  const { userDetails } = useAuth();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checked, setChecked] = useState<Record<number, boolean>>(() => initialCheckedFromStatus(undefined));
  const [updating, setUpdating] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [ifcOutputUrl, setIfcOutputUrl] = useState<string | undefined>(undefined);

  const isEditable = userDetails?.type === "0";
  const userType = userDetails?.type != null ? String(userDetails.type) : undefined;
  const isUserType2 = userType === "2";
  const reportFileUrl = ifcOutputUrl ?? (project ? getReportDownloadUrl(project) : undefined);
  const buttonLabel = userDetails?.type === "0" ? "UPDATE PROGRESS" : "Track progress";

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setError("No project ID");
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProjectById(id)
      .then((data) => {
        if (!cancelled) {
          setProject(data);
          setChecked(initialCheckedFromStatus(data?.status));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError("Failed to load project.");
          setProject(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;

    const fetchLatest = async () => {
      try {
        const latest = await getProjectById(id);
        if (!cancelled) {
          setProject(latest);
          setChecked(initialCheckedFromStatus(latest?.status));
        }
      } catch {
        // Keep previous UI state; this polling should be best-effort.
      }
    };

    // Keep status in sync with background IFC processing updates.
    const intervalId = window.setInterval(fetchLatest, 5000);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [id]);

  useEffect(() => {
    if (!id || !isUserType2) return;
    let cancelled = false;

    const loadIfcOutputPreview = async () => {
      try {
        const reports = await getProjectIFCModelReport(id);
        if (cancelled || !Array.isArray(reports)) return;
        const firstReady = reports.find(
          (item) => Boolean((item as { ifcModelReportPreview?: string }).ifcModelReportPreview),
        ) as { ifcModelReportPreview?: string } | undefined;
        setIfcOutputUrl(firstReady?.ifcModelReportPreview);
      } catch {
        // Keep fallback URL logic from project payload.
      }
    };

    void loadIfcOutputPreview();
  }, [id, isUserType2, project?.status]);

  const handleToggle = (stageId: number) => {
    if (!isEditable) return;
    setChecked((prev) => {
      const next = { ...prev };
      const currentlyChecked = prev[stageId];
      if (currentlyChecked) {
        for (let i = stageId; i <= MAX_STAGE_ID; i++) next[i] = false;
      } else {
        for (let i = MIN_STAGE_ID; i <= stageId; i++) next[i] = true;
      }
      return next;
    });
  };

  const handleUpdateProgress = async () => {
    if (!id || !project) return;
    const foundStage = STAGES.slice().reverse().find((s) => checked[s.id]);
    const highestChecked = foundStage ? foundStage.id : undefined;
    const status = highestChecked ? STAGE_ID_TO_STATUS[highestChecked] : STAGES[0].key;
    setUpdating(true);
    try {
      await updateProjectStatus(id, status);
      setProject((p) => (p ? { ...p, status: status as Project["status"] } : null));
      toast.success("Progress updated");
    } catch (err) {
      toast.error(extractApiError(err));
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!reportFileUrl || !project || !isSafeUrl(reportFileUrl)) return;
    setDownloadLoading(true);
    try {
      const response = await fetch(reportFileUrl, { mode: "cors" });
      if (!response.ok) throw new Error("Download failed");
      const fileName = getFileNameFromDownloadResponse(response, reportFileUrl, "report");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded.");
    } catch (err) {
      try {
        const link = document.createElement("a");
        link.href = reportFileUrl;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Opening report.");
      } catch {
        toast.error("Failed to download report.");
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  if (!id) {
    return (
      <div className="min-h-full relative overflow-hidden">

        <div className="relative z-10 pt-0 pb-8 sm:pb-12 sm:px-6 lg:px-0">
          <Link
            to="/track-audits"
            className="inline-flex items-center gap-2 font-medium text-base sm:text-lg hover:underline mb-6 sm:mb-8 lg:mb-[40px]"
            style={{ color: ACCENT }}
          >
            <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={2} />
            Back to Track Audits
          </Link>
          <p className="text-white/80">No project selected.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-full relative overflow-hidden">
        <div className="relative z-10 pt-0 pb-8 sm:pb-12 sm:px-6 lg:px-0">
          <UserProfileCard />
          <Link
            to="/track-audits"
            className="inline-flex items-center gap-2 font-medium text-base sm:text-lg hover:underline mb-6 sm:mb-8 lg:mb-[40px]"
            style={{ color: ACCENT }}
          >
            <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={2} />
            Back to Track Audits
          </Link>
          <div className="flex flex-col items-center justify-center py-16">
            <div className="relative">
              <div className="w-20 h-20 border-[3px] border-[#E761A0]/20 border-t-[#E761A0] rounded-full animate-spin" />
              <LayoutGrid className="w-8 h-8 text-[#E761A0] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-white/90 text-base mt-5 font-medium">
              Loading...
            </p>
            <p className="text-gray-500 text-sm mt-1">
              Fetching progress details
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-full relative overflow-hidden">
        <div className="relative z-10 pt-0 pb-8 sm:pb-12 sm:px-6 lg:px-0">
          <Link
            to="/track-audits"
            className="inline-flex items-center gap-2 font-medium text-base sm:text-lg hover:underline mb-6 sm:mb-8 lg:mb-[40px]"
            style={{ color: ACCENT }}
          >
            <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={2} />
            Back to Track Audits
          </Link>
          <p className="text-red-400">{error || "Project not found."}</p>
        </div>
      </div>
    );
  }

  const projectImage = (project.projectThumbnailPreview ?? project.projectThumbnail) || DEFAULT_PROJECT_IMAGE;

  const highestCheckedStage = STAGES.slice().reverse().find((s) => checked[s.id]);
  const currentStageId = highestCheckedStage ? highestCheckedStage.id : 0;
  const currentStage = currentStageId ? (STAGES.find((s) => s.id === currentStageId) || null) : null;
  const graphStageLabel = currentStage ? `Stage ${currentStage.id}:` : "—";
  const graphStageName = currentStage
    ? (currentStage.label.includes(": ") ? currentStage.label.split(": ").slice(1).join(": ").trim() : currentStage.label)
    : "Not started";

  return (
    <div className="min-h-full relative overflow-hidden">
      {/* Purple dotted pattern from bottom-left toward center-right */}
      <div
        className="pointer-events-none absolute inset-0 opacity-60"

      />

      <div className="relative z-10 pt-0 pb-8 sm:pb-12 sm:px-6 lg:px-0">
        <UserProfileCard />
        <Link
          to="/track-audits"
          className="inline-flex items-center gap-2 font-medium text-base sm:text-lg hover:underline mb-6 sm:mb-8 lg:mb-[40px]"
          style={{ color: ACCENT }}
        >
          <ArrowLeft className="w-5 h-5 shrink-0" strokeWidth={2} />
          Back to Track Audits
        </Link>

        <div
          className="rounded-[12px] sm:rounded-[15px] border overflow-hidden flex flex-col-reverse lg:flex-row"
          style={{
            borderColor: BORDER_LIGHT,
          }}
        >
          {/* Left section: project info */}
          <div className="p-5 sm:p-6 md:p-8 md:pr-10 lg:p-[56px_41px_53px_53px] lg:pr-[41px] flex flex-col w-full max-w-full lg:max-w-[calc(100%-400px)] 2xl:max-w-[calc(100%-530px)] min-w-0">
            <h1
              className="text-[28px] md:text-[34px] lg:text-[44px] font-semibold mb-0 sm:mb-6 lg:mb-[29px]"
              style={{ color: ACCENT }}
            >
              {project.name}
            </h1>

            <div className="text-white font-light text-base sm:text-lg leading-[1.65] space-y-3 sm:space-y-4 ">
              <p>
                {project.summary ||
                  "No description."}
              </p>
            </div>
            <div className="mt-6 sm:mt-8 w-full max-w-[494px] aspect-[4/3] rounded-[10px] sm:rounded-[12px] overflow-hidden border border-white/10 flex-shrink-0 h-[180px] sm:h-[294px]">
              <img
                src={projectImage || undefined}
                alt={project.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Vertical divider */}
          <div
            className="hidden lg:block w-px flex-shrink-0 self-stretch"
            style={{ background: DIVIDER }}
          />

          {/* Right section: stages + download */}
          <div className="flex flex-col items-center lg:items-start flex-1 min-w-0">
            <div className="flex flex-col items-center w-full pb-6 sm:pb-8 lg:pb-[38px]">
              <div className="p-5 sm:p-6 md:p-8 lg:p-[53px_53px_0_53px] w-full">
                <div className="relative flex flex-col items-center justify-center mb-6 sm:mb-8">
                  {/* Pie-chart style progress: e.g. stage 2 = 40% */}
                  <div className="w-full max-w-[240px] sm:max-w-[280px] md:max-w-[306px] lg:w-[326px] lg:max-w-none aspect-square">
                    <StageProgressGraph
                      completedStages={currentStageId}
                      totalStages={STAGE_COUNT}
                      size={100}
                      strokeWidth={12}
                    />
                  </div>
                  <div className="mt-3 flex flex-col items-center justify-center px-4 text-center">
                    <p
                      className="text-sm sm:text-base lg:text-lg font-semibold leading-tight"
                      style={{ color: ACCENT }}
                    >
                      {graphStageLabel}
                    </p>
                    <p
                      className="text-sm sm:text-base lg:text-lg font-semibold leading-tight text-white/90"
                    >
                      {graphStageName}
                    </p>
                  </div>
                </div>

                <ul className="w-full space-y-3 sm:space-y-3 mb-6 sm:mb-[35px] max-w-full lg:max-w-fit">
                  {STAGES.map((stage) => {
                    const isChecked = checked[stage.id];
                    return (
                      <li
                        key={stage.id}
                        className={`flex items-start sm:items-center gap-3 sm:gap-[15px] text-white min-w-0 ${isChecked ? "font-semibold" : "font-light"}`}
                      >
                        {isEditable ? (
                          <label className="flex items-start sm:items-center gap-3 sm:gap-[15px] cursor-pointer w-full min-w-0">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggle(stage.id)}
                              className="sr-only"
                              aria-checked={isChecked}
                            />
                            <span
                              className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 border-2 box-border transition-colors mt-0.5 sm:mt-0"
                              style={{
                                borderColor: isChecked ? "transparent" : ACCENT,
                                background: isChecked ? ACCENT : "transparent",
                              }}
                            >
                              {isChecked && (
                                <Check
                                  className="w-3.5 h-3.5 text-white"
                                  strokeWidth={3}
                                />
                              )}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-base sm:text-lg break-words">{stage.label}</span>
                              {stage.description && (
                                <span className="block text-sm text-white/70 font-normal mt-0.5">{stage.description}</span>
                              )}
                            </span>
                          </label>
                        ) : (
                          <div className="flex items-start sm:items-center gap-3 sm:gap-[15px] w-full min-w-0">
                            <span
                              className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 border-2 box-border transition-colors mt-0.5 sm:mt-0"
                              style={{
                                borderColor: isChecked ? "transparent" : ACCENT,
                                background: isChecked ? ACCENT : "transparent",
                              }}
                            >
                              {isChecked && (
                                <Check
                                  className="w-3.5 h-3.5 text-white"
                                  strokeWidth={3}
                                />
                              )}
                            </span>
                            <span className="flex-1 min-w-0">
                              <span className="block text-base sm:text-lg break-words">{stage.label}</span>
                              {stage.description && (
                                <span className="block text-sm text-white/70 font-normal mt-0.5">{stage.description}</span>
                              )}
                            </span>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="border-t border-[#868686] pt-5 sm:pt-6 lg:pt-[27px] w-full text-center px-4 sm:px-0">
                {isUserType2 ? (
                  <button
                    type="button"
                    disabled={!reportFileUrl || downloadLoading}
                    className="
                    text-[13px] sm:text-[14px] xl:text-[16px] font-semibold
                    group overflow-hidden flex items-center justify-center gap-2 px-6 py-3 sm:px-9 sm:py-[10px]
                    rounded-[27px] border-2 border-[#E761A0]
                    bg-[#E761A0] text-white
                    transition-all duration-300 ease-out
                    hover:bg-transparent btn-hover-color mx-auto min-h-[44px] sm:min-h-0 disabled:opacity-50 disabled:cursor-not-allowed
                  "
                    onClick={handleDownloadReport}
                  >
                    {downloadLoading ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <DownloadIcon />
                    )}
                    {downloadLoading ? "Downloading..." : "Download report"}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={updating}
                    className="
                    text-[13px] sm:text-[14px] xl:text-[16px] font-semibold
                    group overflow-hidden flex items-center justify-center gap-2 px-6 py-3 sm:px-9 sm:py-[10px]
                    rounded-[27px] border-2 border-[#E761A0]
                    bg-[#E761A0] text-white
                    transition-all duration-300 ease-out
                    hover:bg-transparent btn-hover-color mx-auto min-h-[44px] sm:min-h-0 disabled:opacity-50 disabled:cursor-not-allowed
                  "
                    onClick={handleUpdateProgress}
                  >
                    {updating ? (
                      <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <TrackProgress />
                    )}
                    {updating ? "Updating..." : buttonLabel}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IFCProcessing;
